import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EAutocompleteOption} from 'webapp-lib/Components';
import {useDebouncedValue} from 'webapp-lib/utils';

import {EFTagsAutocomplete} from '#/Autocomplete/ETagsAutocomplete';
import {CasusSearchItem} from '@lexian/api/casus-api';
import {assignableCasusFilters} from '_/store/searchCasus';
import {SearchCasusFilters} from '_/types/casus';
import {EFAutocomplete, useWatch} from '_/utilities/form';
import {PaginatedItemLoader} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PageItem,
  PaginationDirection,
  PaginationMode,
} from '_/utilities/types';

import {OptionFilterInfo} from '../OptionFilterInfo/OptionFilterInfo';

import {useCasusSearchFilters} from './CasusAutocomplete.hooks';
import {
  CasusAutocompleteProps,
  MinimalCasusSearchItem,
} from './CasusAutocomplete.types';
import {
  buildSearchCasus,
  CASUS_CATEGORIES,
  mapStateFilter,
  mapToOption,
} from './CasusAutocomplete.utils';

function UCasusAutocomplete<
  T extends MinimalCasusSearchItem = CasusSearchItem,
>({
  filters = assignableCasusFilters,
  searchFiltersPersistenceKey,
  onSelectedCasusIsFilteredOut,
  disableUnassignableCasus = false,
  onCasusChange,
  multiple = false,
  recommendedOptions,
  categories = CASUS_CATEGORIES,
  ...otherProps
}: CasusAutocompleteProps<T>): React.ReactElement {
  const {t} = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);

  const [debounceSearchQuery, searchQuery, setSearchQuery] =
    useDebouncedValue('');
  const lastOnCasusChangeId = useRef<string>();
  const selectedCasusId = useWatch({
    control: otherProps.control,
    name: otherProps.name,
    defaultValue: otherProps.defaultValue,
  });

  function checkIfSelectedCasusIsInData() {
    if (!onSelectedCasusIsFilteredOut || !selectedCasusId) {
      return;
    }

    const isSelectedCasusInData = casusList.loadable.data?.some(
      ({item}) => item.id === selectedCasusId
    );
    if (!isSelectedCasusInData) {
      onSelectedCasusIsFilteredOut();
      inputRef.current?.focus();
    }
    setShowFilteredOutWarning(!isSelectedCasusInData);
  }

  const {
    persistenceInitialized,
    filtersModified,
    searchFilters,
    searchFilterText,
    showSearchFiltersDialog,
    searchFiltersDialog,
  } = useCasusSearchFilters({
    persistenceKey: searchFiltersPersistenceKey,
    showCasusFilteredOutInfo: onSelectedCasusIsFilteredOut !== undefined,
  });
  const [showFilteredOutWarning, setShowFilteredOutWarning] = useState(false);

  const [casusList] = useState(() => new PaginatedItemLoader(buildSearchCasus));

  const searchCasusFilters = useMemo(
    (): SearchCasusFilters => ({
      ...filters,
      ...searchFilters,
    }),
    [filters, searchFilters]
  );

  const casusOptions = useMemo((): EAutocompleteOption[] => {
    const filteredRecommendedOptions =
      recommendedOptions?.filter(
        (it) =>
          it.displayNameWithClient
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) &&
          it.active === mapStateFilter(searchCasusFilters.stateFilters)
      ) ?? [];

    const filteredOptions = filterOutRecommendedOptions(
      casusList.loadable.data || [],
      filteredRecommendedOptions
    ).map((it) => it.item);

    return [
      ...(!casusList.loadable.hasMoreBefore ? filteredRecommendedOptions : []),
      ...filteredOptions,
    ].map((it) => mapToOption(it, disableUnassignableCasus));
  }, [
    casusList.loadable.data,
    casusList.loadable.hasMoreBefore,
    disableUnassignableCasus,
    recommendedOptions,
    searchCasusFilters.stateFilters,
    searchQuery,
  ]);

  const onLoadMoreCasusBefore = useCallback(async () => {
    await casusList.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Backward},
      {filters: searchCasusFilters, searchQuery}
    );
  }, [casusList, searchCasusFilters, searchQuery]);

  const onLoadMoreCasusAfter = useCallback(async () => {
    await casusList.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
      {filters: searchCasusFilters, searchQuery}
    );
  }, [casusList, searchCasusFilters, searchQuery]);

  // handle casus change selection logic
  useEffect(() => {
    if (!onCasusChange || selectedCasusId === lastOnCasusChangeId.current) {
      return;
    }
    if (selectedCasusId) {
      const selectedRecommendedOption = (recommendedOptions ?? []).find(
        (casus) => casus.id === selectedCasusId
      );

      const selectedCasus = (casusList.loadable.data || [])
        .map((it) => it.item)
        .find((casus) => casus.id === selectedCasusId);

      lastOnCasusChangeId.current =
        selectedCasus?.id ?? selectedRecommendedOption?.id;

      const selection = selectedCasus ?? selectedRecommendedOption;

      // The cast is necessary because the server call cannot be made
      // generic on the type T.
      onCasusChange(selection as MinimalCasusSearchItem as T);
    } else {
      lastOnCasusChangeId.current = undefined;
      onCasusChange(undefined);
    }
  }, [
    casusList.loadable.data,
    recommendedOptions,
    disableUnassignableCasus,
    onCasusChange,
    selectedCasusId,
  ]);

  async function fetchData() {
    if (selectedCasusId && !debounceSearchQuery && !multiple) {
      await casusList.fetch(
        {
          mode: PaginationMode.Navigate,
          key: [selectedCasusId],
        },
        {filters: searchCasusFilters, searchQuery: ''}
      );
    } else {
      await casusList.fetch(defaultPaginationArguments, {
        filters: searchCasusFilters,
        searchQuery: debounceSearchQuery,
      });
    }

    checkIfSelectedCasusIsInData();
  }

  // handle fetching logic
  useEffect(() => {
    if (!persistenceInitialized) {
      return;
    }

    fetchData();
    // fetch data only on filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    persistenceInitialized,
    filtersModified,
    debounceSearchQuery,
    searchCasusFilters,
  ]);

  const optionFilterInfo = (
    <OptionFilterInfo
      filteredOutWarningText={t('casusAutocomplete.filteredOutWarningText')}
      optionFilterText={searchFilterText}
      showFilteredOutWarning={showFilteredOutWarning}
    />
  );

  const optionDisabledReason = disableUnassignableCasus
    ? t('casusAutocomplete.optionDisabledReason')
    : undefined;

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          categories={categories}
          hasMoreAfter={casusList.loadable.hasMoreAfter}
          hasMoreBefore={casusList.loadable.hasMoreBefore}
          inputRef={inputRef}
          labelInfo={optionDisabledReason}
          optionFilterInfo={optionFilterInfo}
          options={casusOptions}
          noOptionFiltering
          onFilterClick={showSearchFiltersDialog}
          onLoadMoreAfter={onLoadMoreCasusAfter}
          onLoadMoreBefore={onLoadMoreCasusBefore}
          onSearchQueryChange={setSearchQuery}
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          categories={categories}
          hasMoreAfter={casusList.loadable.hasMoreAfter}
          hasMoreBefore={casusList.loadable.hasMoreBefore}
          inputRef={inputRef}
          labelInfo={optionDisabledReason}
          optionFilterInfo={optionFilterInfo}
          options={casusOptions}
          noOptionFiltering
          onFilterClick={showSearchFiltersDialog}
          onLoadMoreAfter={onLoadMoreCasusAfter}
          onLoadMoreBefore={onLoadMoreCasusBefore}
          onSearchQueryChange={setSearchQuery}
        />
      )}
      {searchFiltersDialog}
    </>
  );
}
export const CasusAutocomplete = observer(UCasusAutocomplete);

function filterOutRecommendedOptions(
  data: PageItem<CasusSearchItem>[],
  recommendedOptions: MinimalCasusSearchItem[] | undefined
) {
  if (!recommendedOptions || recommendedOptions.length === 0) {
    return data;
  }
  const firstPageCasusIds = recommendedOptions.map((it) => it.id);
  return data.filter(({item}) => !firstPageCasusIds.includes(item.id));
}
